import { Button, Chip, Field, formatTime, Panel } from "@kitchen/ui";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { api, type StaffRow } from "../api.js";
import { askReason, ErrorLine, useAction } from "./common.js";

interface ScreenRow {
  id: string;
  version: number;
  name: string;
  responsiblePersonIds: string[];
  silentAfterSeconds: number;
  lastBeatAt: string | null;
  silentSince: string | null;
}

const SCREEN_KEY = "kitchen.screenId";
export const thisDeviceScreen = (): string | null => {
  try {
    return localStorage.getItem(SCREEN_KEY);
  } catch {
    return null;
  }
};

/** While this device is an alert screen, it beats every 30 s (F3-T11, architecture §9.7). */
export function useScreenBeat(enabled: boolean) {
  useEffect(() => {
    const id = thisDeviceScreen();
    if (!enabled || !id) return;
    const beat = () => void api(`/screens/${id}/beat`, { body: {} }).catch(() => undefined);
    beat();
    const t = setInterval(beat, 30_000);
    return () => {
      clearInterval(t);
    };
  }, [enabled]);
}

/**
 * F3-T11: the manager lists the alert screens (chef's board, packing, office) and who answers for each. A screen
 * that stops answering alerts them at once, also by SMS (DL-102). "This device" turns the current device into one.
 */
export function ScreensPanel() {
  const screens = useQuery({
    queryKey: ["screens"],
    queryFn: () => api<ScreenRow[]>("/screens"),
    refetchInterval: 30_000,
  });
  const staff = useQuery({ queryKey: ["staff"], queryFn: () => api<StaffRow[]>("/staff") });
  const [name, setName] = useState("");
  const [people, setPeople] = useState<string[]>([]);
  const [mine, setMine] = useState(thisDeviceScreen);
  const create = useAction(async () => {
    await api("/screens", { body: { name, responsiblePersonIds: people } });
    setName("");
    setPeople([]);
  }, [["screens"]]);
  const archive = useAction(
    async (s: ScreenRow) => {
      const reason = askReason(s.name);
      if (reason) await api(`/screens/${s.id}/archive`, { body: { version: s.version, reason } });
    },
    [["screens"]],
  );
  const person = (id: string) => staff.data?.find((p) => p.id === id)?.displayName ?? "—";
  const setThisDevice = (id: string | null) => {
    try {
      if (id) localStorage.setItem(SCREEN_KEY, id);
      else localStorage.removeItem(SCREEN_KEY);
    } catch {
      // storage blocked: nothing to remember
    }
    setMine(id);
    window.location.reload();
  };

  return (
    <Panel title="صفحه‌های اعلان">
      <div className="stack">
        <p className="t-cap muted">
          هر صفحه‌ای که نباید هشداری را از دست بدهد (تابلوی سرآشپز، بسته‌بندی، دفتر) اینجا ثبت می‌شود. اگر بیش
          از دو دقیقه پاسخ ندهد، مسئولانش فوراً در برنامه و با پیامک خبردار می‌شوند.
        </p>
        {(screens.data ?? []).map((s) => (
          <div key={s.id} className="card row wrap" style={{ gap: "var(--s2)" }}>
            <div className="grow">
              <div className="m">{s.name}</div>
              <div className="t-cap muted">
                {s.responsiblePersonIds.map(person).join("، ")}
                {s.lastBeatAt && ` · آخرین پاسخ ${formatTime(s.lastBeatAt)}`}
              </div>
            </div>
            {s.silentSince ? (
              <Chip tone="coral">پاسخ نمی‌دهد</Chip>
            ) : s.lastBeatAt ? (
              <Chip tone="ok">وصل</Chip>
            ) : (
              <Chip plain>هنوز وصل نشده</Chip>
            )}
            {mine === s.id ? (
              <Button
                variant="ghost"
                onClick={() => {
                  setThisDevice(null);
                }}
              >
                این دستگاه دیگر نیست
              </Button>
            ) : (
              <Button
                onClick={() => {
                  setThisDevice(s.id);
                }}
              >
                این دستگاه همین صفحه است
              </Button>
            )}
            <Button
              variant="ghost"
              onClick={() => {
                archive.run(s);
              }}
            >
              بایگانی
            </Button>
          </div>
        ))}
        <form
          className="stack"
          onSubmit={(e) => {
            e.preventDefault();
            create.run(undefined);
          }}
        >
          <Field
            id="screen-name"
            label="نام صفحه‌ی تازه"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
            }}
          />
          <div className="field">
            <label htmlFor="screen-people">مسئولان (با Ctrl چند نفر را انتخاب کنید)</label>
            <select
              id="screen-people"
              className="input"
              multiple
              value={people}
              onChange={(e) => {
                setPeople([...e.target.selectedOptions].map((o) => o.value));
              }}
            >
              {(staff.data ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.displayName}
                </option>
              ))}
            </select>
          </div>
          <Button type="submit" icon="plus" disabled={!name.trim() || people.length === 0 || create.busy}>
            افزودن صفحه
          </Button>
        </form>
        <ErrorLine error={create.error ?? archive.error} />
      </div>
    </Panel>
  );
}
